import React, { useState } from "react";
import { Badge, Button, CellText, DotsVertical, Edit01, EmptyState, Menu, Plus, Receipt, Table, Trash01 } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import SeriesPassSurchargeModal from "./SeriesPassSurchargeModal";
import ConfirmDialog from "./ConfirmDialog";
import { fmtPrice } from "../passFormatting";
import { toastError } from "../../../shared/toastHelper";

const SeriesPassSurchargesCard = () => {
  const { series, handleDeleteSurcharge } = useSeriesShowContext();
  const surcharges = series.series_pass_surcharges || [];
  const currency = series.base_currency_symbol;
  const [modalOpen, setModalOpen] = useState(false);
  const [editSurcharge, setEditSurcharge] = useState(null);
  const [deleteSurcharge, setDeleteSurcharge] = useState(null);

  const openCreate = () => {
    setEditSurcharge(null);
    setModalOpen(true);
  };

  const openEdit = (surcharge) => {
    setEditSurcharge(surcharge);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditSurcharge(null);
  };

  const confirmDelete = async (surcharge) => {
    try {
      await handleDeleteSurcharge(surcharge.id);
    } catch (e) {
      const apiErrors = e && e.response && e.response.data;
      toastError(
        Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors.join(", ")
          : "Could not delete this fee. Please try again.",
      );
    }
  };

  const deleteBody = (surcharge) => {
    if (!surcharge) return "";
    if (surcharge.in_use) {
      return `"${surcharge.name}" is already on orders, so it can't be deleted. Edit it and turn it off to stop charging it.`;
    }
    return `Delete "${surcharge.name}"? This can't be undone.`;
  };

  const columns = [
    { key: "name", header: "Label", primary: true, render: (surcharge) => <CellText primary={surcharge.name} /> },
    {
      key: "amount",
      header: "Per pass or pack",
      align: "right",
      render: (surcharge) => (
        <span data-testid={`surcharge-amount-${surcharge.id}`}>{fmtPrice(surcharge.default_amount, currency)}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (surcharge) => (
        <span data-testid={`surcharge-status-${surcharge.id}`}>
          {surcharge.enabled ? <Badge color="success">Charging</Badge> : <Badge color="gray">Off</Badge>}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (surcharge) => (
        <Menu
          placement="bottom-end"
          items={[
            { label: "Edit fee", icon: Edit01, onClick: () => openEdit(surcharge) },
            { label: "Delete fee", icon: Trash01, destructive: true, onClick: () => setDeleteSurcharge(surcharge) },
          ]}
          trigger={(
            <Button
              variant="tertiary"
              size="sm"
              icon={DotsVertical}
              aria-label={`Actions for ${surcharge.name}`}
              data-testid={`surcharge-actions-btn-${surcharge.id}`}
            />
          )}
        />
      ),
    },
  ];

  return (
    <div className="series-schedule-card mt-4" data-testid="surcharges-card">
      <div className="series-schedule-header d-flex align-items-center justify-content-between">
        <h4 className="series-schedule-title mb-0">Custom Fees & Surcharges</h4>
        {series.state !== "canceled" && (
          <Button
            variant="secondary"
            icon={Plus}
            data-testid="create-surcharge-btn"
            onClick={openCreate}
          >
            Add fee
          </Button>
        )}
      </div>

      <div data-testid={surcharges.length === 0 ? "surcharges-empty" : "surcharges-table"}>
        <Table
          columns={columns}
          data={surcharges}
          getRowKey={(surcharge) => surcharge.id}
          empty={(
            <EmptyState
              size="sm"
              icon={Receipt}
              title="No custom fees"
              description="Add a fee or surcharge to every pass and pack sold in this series. Each one is charged once per pass or pack and shows as its own line at checkout."
            />
          )}
        />
      </div>

      <SeriesPassSurchargeModal open={modalOpen} surcharge={editSurcharge} onClose={closeModal} />
      <ConfirmDialog
        open={deleteSurcharge !== null}
        title="Delete this fee?"
        body={deleteBody(deleteSurcharge)}
        confirmLabel="Delete fee"
        confirmDisabled={deleteSurcharge !== null && deleteSurcharge.in_use}
        onConfirm={() => confirmDelete(deleteSurcharge)}
        onClose={() => setDeleteSurcharge(null)}
      />
    </div>
  );
};

export default SeriesPassSurchargesCard;
